/**
 * Copyright (c) Simam Digital. Built on Project Flowerbed
 * (Copyright (c) Meta Platforms, Inc. and affiliates).
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import * as THREE from 'three';

import {
	EnvironmentProp,
	MainEnvironment,
} from '../../components/GameObjectTagComponents';

import { AssetDatabaseComponent } from '../../components/AssetDatabaseComponent';
import { AssetURLs } from '@config/AssetURLs';
import { GameStateComponent } from '../../components/GameStateComponent';
import { MeshIdComponent } from '../../components/AssetReplacementComponents';
import { Object3DComponent } from '../../components/Object3DComponent';
import { PlayerStateComponent } from '../../components/PlayerStateComponent';
import { System } from 'ecsy';
import { THREEGlobalComponent } from '../../components/THREEGlobalComponent';
import { getOnlyEntity } from '../../utils/entityUtils';

/**
 * A little "welcome garden" of extra decorative props placed around the player's
 * spawn, using models already shipped with the island. Purely cosmetic — it
 * enriches the arrival area without touching the base environment asset.
 *
 * Every prop is grounded by raycasting straight down onto the environment, so
 * nothing ever floats: if a spot has no ground beneath it (e.g. over water), the
 * prop is simply skipped. The whole pass runs once, a moment after the
 * environment finishes loading, and is wrapped so any failure leaves the scene
 * untouched rather than breaking the frame.
 */
const DECOR_LAYOUT = [
	// [meshId, radius (m from spawn), angle (deg), yawFacesCenter]
	['Prop_Gazebo', 9, 180, true],
	['Prop_Planter_Circular_01', 4, 35, false],
	['Prop_Planter_Circular_02', 4, -35, false],
	['Prop_Planter_Long01', 6, 90, true],
	['Prop_Planter_Long02', 6, -90, true],
	['Bench', 5, 20, true],
	['Bench', 5, -20, true],
	['Prop_Pergola', 11, 150, true],
	['Prop_Pergola', 11, -150, true],
];

export class EnvironmentDecorSystem extends System {
	init() {
		this._done = false;
		this._settle = 0;
		this._loadStarted = false;
		this._loaded = false;
	}

	execute(delta) {
		if (this._done) return;

		const gameManager = getOnlyEntity(this.queries.gameManager, false);
		const environment = getOnlyEntity(this.queries.environment, false);
		const player = getOnlyEntity(this.queries.player, false);
		const assetEntity = getOnlyEntity(this.queries.assetDatabase, false);
		if (!gameManager || !environment || !player || !assetEntity) return;

		// Let the environment settle into the scene graph for a beat so raycasts
		// hit real geometry with up-to-date world matrices.
		this._settle += delta;
		if (this._settle < 1.0) return;

		const assetDatabase = assetEntity.getComponent(AssetDatabaseComponent);

		// The decorative props aren't part of the base mesh manifest, so pull the
		// ones we need into the mesh database on demand. We only place them once
		// they've finished loading, and only the ones that actually resolved.
		if (!this._loadStarted) {
			this._loadStarted = true;
			this._ensureMeshesLoaded(assetDatabase.meshes)
				.catch((e) =>
					console.warn('[Decor] Failed to load decoration meshes', e),
				)
				.finally(() => {
					this._loaded = true;
				});
			return;
		}
		if (!this._loaded) return;

		this._done = true;
		try {
			this._placeDecor(gameManager, player, assetDatabase.meshes);
		} catch (e) {
			console.warn('[Decor] Skipped environment decoration', e);
		}
	}

	/**
	 * Loads any decor prop meshes that aren't already in the database, from the
	 * shared props directory. LODs are picked up automatically via LODConfigs.
	 */
	_ensureMeshesLoaded(meshDatabase) {
		const dir = AssetURLs.ADDITIONAL_MESH_DIRS[0];
		const ids = [...new Set(DECOR_LAYOUT.map(([meshId]) => meshId))];
		const pending = ids
			.filter((id) => !meshDatabase.meshes[id])
			.map((id) => meshDatabase.load(id, `${dir}${id}.gltf`));
		return Promise.all(pending);
	}

	_placeDecor(gameManager, player, meshDatabase) {
		const three = gameManager.getComponent(THREEGlobalComponent);
		const scene = three.scene;
		const spawn = player
			.getComponent(PlayerStateComponent)
			.viewerTransform.position.clone();

		const raycaster = new THREE.Raycaster();
		const down = new THREE.Vector3(0, -1, 0);
		let placed = 0;

		DECOR_LAYOUT.forEach(([meshId, radius, angleDeg, faceCenter]) => {
			// Skip anything that failed to load so we never hand an unresolved
			// mesh id to AssetReplacementSystem (which would crash on it).
			if (!meshDatabase.meshes[meshId]) return;

			const angle = (angleDeg * Math.PI) / 180;
			const x = spawn.x + Math.cos(angle) * radius;
			const z = spawn.z + Math.sin(angle) * radius;

			const ground = this._groundAt(raycaster, down, scene, x, z, spawn.y);
			if (!ground) return; // no ground here (e.g. over water) -> skip.

			const placeholder = new THREE.Object3D();
			placeholder.position.copy(ground);
			if (faceCenter) {
				placeholder.rotation.y = Math.atan2(spawn.x - x, spawn.z - z);
			} else {
				placeholder.rotation.y = Math.random() * Math.PI * 2;
			}
			scene.add(placeholder);

			const entity = this.world.createEntity();
			entity.addComponent(Object3DComponent, { value: placeholder });
			entity.addComponent(MeshIdComponent, { id: meshId });
			entity.addComponent(EnvironmentProp);
			placed++;
		});

		console.info(
			`[Decor] Placed ${placed}/${DECOR_LAYOUT.length} welcome props.`,
		);
	}

	/**
	 * Raycast straight down onto the environment and return the ground point at
	 * (x, z), or null if nothing solid is below. Sky, water and clouds are
	 * ignored so props sit on real terrain.
	 * @returns {THREE.Vector3|null}
	 */
	_groundAt(raycaster, down, scene, x, z, nearY) {
		raycaster.set(new THREE.Vector3(x, nearY + 40, z), down);
		raycaster.far = 120;
		const hits = raycaster.intersectObjects(scene.children, true);
		for (const hit of hits) {
			const name = (hit.object.name || '').toLowerCase();
			const matName = (hit.object.material?.name || '').toLowerCase();
			if (/sky|cloud|water|player|controller|ray|ring/.test(name)) continue;
			if (/sky|cloud|water/.test(matName)) continue;
			return hit.point.clone();
		}
		return null;
	}
}

EnvironmentDecorSystem.queries = {
	gameManager: { components: [THREEGlobalComponent, GameStateComponent] },
	environment: { components: [MainEnvironment] },
	player: { components: [PlayerStateComponent] },
	assetDatabase: { components: [AssetDatabaseComponent] },
};
